/**
 * AccountingViewCockpit — full Cockpit-language structural rewrite of /accounting.
 *
 * Mirrors AccountingView's logic (jobs + financials + credits + filters)
 * but composes only Cockpit shell primitives so the visual language is
 * guaranteed regardless of skin tokens. No business logic changes.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { VarianceDetailDrawer, type VarianceDetailContext } from "@/components/finance/VarianceDetailDrawer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { exportCsv, stampFilename } from "@/lib/csvExport";
import type { VarianceSeverity } from "@/data/finance/payRun/varianceAnalysis";
// import type { PayRunHoldReason } from "@/data/finance/payRun/getJobPayRun";
import { HOLD_REASON_LABEL, HOLD_REASON_OWNER } from "@/data/finance/payRun/holdReasonCopy";
import { formatHeldAge, getHeldAgeTone, type PayRunHoldAge } from "@/data/finance/payRun/getPayRunHoldAge";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { useExpenseLedgerContext } from "@/data/jobCost/ExpenseLedgerContext";
import { useRole } from "@/data/RoleContext";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { canReadExpenseLedger } from "@/data/jobCost/expenseLedgerReader";
import {
  BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState,
} from "@/components/BranchRegionFilter";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitKpiStrip, CockpitFilterBar, CockpitChipGroup, CockpitSearch,
  CockpitFinancialTable, type CockpitColumn, CockpitSection, StatusDot,
} from "@/components/cockpit/CockpitShell";

// type FilterKey = "all" | "unpaid" | "overdue" | "payout-pending" | "paid";
// const CURRENT_REP = "Mike Johnson";
import { FinanceCommandCenter } from "@/components/finance/FinanceCommandCenter";
import { OperationalLedger } from "@/components/finance/OperationalLedger";
import { usePayRunViews } from "@/data/finance/payRun/usePayRunViews";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution";
import { deriveAllDisputes } from "@/data/contractor/networkExecution";
import { getPendingFieldAdds } from "@/data/finance/payRun/fieldAdds";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { PayRunHoldReason } from "@/data/finance/payRun/getJobPayRun";
type FilterKey = "all" | "unpaid" | "overdue" | "held" | "payout-pending" | "paid";
const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

// function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
//   const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
//   return jobNumber || job.id;
// }

type LivePayoutStatus = 
  | "released"
  | "ready"
  | "held"
  | "pending_wrap"
  | "none";

interface Row {
  id: string;
  // displayRef: string;
  customerName: string;
  branch: string;
  contract: number;
  materials: number;
  labor: number;
  sub: number;
  margin: number;
  marginPct: number;
  paymentStatus: string;
  payoutStatus: string;
  liveStatus: LivePayoutStatus;
  liveDetail: string;
  holdReasons: PayRunHoldReason[];
  heldAge: PayRunHoldAge | null;
}

export default function AccountingViewCockpit() {
  const appData = useAppData();
  const { jobs, credits, holdbackLedger, disputeEvents } = appData;
  const payRunRecords: any[] = (appData as any).payRunRecords ?? [];
  const fieldAddSubmissions: any[] = (appData as any).fieldAddSubmissions ?? [];
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [varianceDetail, setVarianceDetail] = useState<VarianceDetailContext | null>(null);
  const fin$ = useFinancialPermissions();
  const { role } = useRole();
  const { repName } = useCurrentRep();
  const { entries: allLedgerEntries } = useExpenseLedgerContext();
  const ledgerAuthorized = canReadExpenseLedger(role);
  const { rows: payRunRows, rowByJob, readyRows, heldRows, varianceRollup, laborSignals, reconciliations } = usePayRunViews();
  if (!fin$.viewFinancialSummary && !fin$.viewContractValue) {
    return (
      <CockpitPage>
      <BooksNavRail />
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="h-12 w-12 rounded-sm border border-destructive/30 flex items-center justify-center mb-4">
            <ShieldAlert className="h-5 w-5 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-muted-foreground max-w-md">
            Your current role does not have permission to view accounting and financial data.
          </p>
        </div>
      </CockpitPage>
    );
  }

  const baseJobs = fin$.scopedToOwn ? jobs.filter((j) => j.salesRep === repName) : jobs;
  const allJobs = filterByBranchRegion(baseJobs, brFilter);
  const jobsWithFinancials = allJobs.filter((j) => j.financials);

  const totalRevenue = jobsWithFinancials.reduce((s, j) => s + j.financials!.contractValue, 0);
  const totalMaterialCost = jobsWithFinancials.reduce((s, j) => s + j.financials!.estimatedMaterialCost, 0);
  const totalLaborCost = jobsWithFinancials.reduce((s, j) => s + j.financials!.estimatedLaborCost, 0);
  const totalSubPayout = jobsWithFinancials.reduce((s, j) => s + j.financials!.subcontractorPayout, 0);
  const totalMargin = jobsWithFinancials.reduce((s, j) => s + j.financials!.marginEstimate, 0);
  const marginPct = totalRevenue > 0 ? ((totalMargin / totalRevenue) * 100).toFixed(1) : "0";

  // Map jobId → most recent PayRunRecord (released history).
  const releasedRecordByJob = new Map<string, typeof payRunRecords[number]>();
  for (const rec of payRunRecords) {
    const prev = releasedRecordByJob.get(rec.jobId);
    if (!prev || rec.releasedAt > prev.releasedAt) releasedRecordByJob.set(rec.jobId, rec);
  }

  interface LiveResolution {
    status: LivePayoutStatus;
    detail: string;
    holdReasons: PayRunHoldReason[];
    heldAge: PayRunHoldAge | null;
  }
  const resolveLive = (jobId: string, subAmount: number): LiveResolution => {
    const released = releasedRecordByJob.get(jobId);
    if (released) return {
      status: "released",
      detail: `${fmt(released.netReleased)} · ${new Date(released.releasedAt).toLocaleDateString()}`,
      holdReasons: [],
      heldAge: null,
    };
    const prv = rowByJob.get(jobId);
    if (prv) {
      if (prv.view.releasable) return {
        status: "ready",
        detail: fmt(prv.view.netReleasable),
        holdReasons: [],
        heldAge: null,
      };
      const reasons = prv.view.holdReasons;
      const age = prv.heldAge ?? null;
      const detail = age ? `Held ${formatHeldAge(age.ageMs)}` : (reasons.length > 0 ? `${reasons.length} hold reason${reasons.length === 1 ? "" : "s"}` : "Held");
      return { status: "held", detail, holdReasons: reasons, heldAge: age };
    }
    if (subAmount > 0) return { status: "pending_wrap", detail: "Awaiting installer wrap", holdReasons: [], heldAge: null };
    return { status: "none", detail: "—", holdReasons: [], heldAge: null };
  };

  const unpaid = jobsWithFinancials.filter((j) => j.financials!.paymentStatus !== "Paid");
  const overdue = jobsWithFinancials.filter((j) => j.financials!.paymentStatus === "Overdue");
  const heldJobs = jobsWithFinancials.filter((j) => resolveLive(j.id, j.financials!.subcontractorPayout).status === "held");
  const payoutPending = jobsWithFinancials.filter((j) => {
    const { status } = resolveLive(j.id, j.financials!.subcontractorPayout);
    return status === "ready" || status === "held" || status === "pending_wrap";
  });
  const paid = jobsWithFinancials.filter((j) => j.financials!.paymentStatus === "Paid");

  let filtered = jobsWithFinancials;
  if (activeFilter === "unpaid") filtered = unpaid;
  if (activeFilter === "overdue") filtered = overdue;
  if (activeFilter === "held") filtered = heldJobs;
  if (activeFilter === "payout-pending") filtered = payoutPending;
  if (activeFilter === "paid") filtered = paid;

  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter((j) =>
      j.id.toLowerCase().includes(s) ||
      j.customerName.toLowerCase().includes(s) ||
      j.branch.toLowerCase().includes(s)
    );
  }

  const rows: Row[] = filtered.map((j) => {
    const f = j.financials!;
    const live = resolveLive(j.id, f.subcontractorPayout);
    return {
      id: j.id,
      customerName: j.customerName,
      branch: j.branch,
      // displayRef: getJobDisplayRef(j),
      contract: f.contractValue,
      materials: f.estimatedMaterialCost,
      labor: f.estimatedLaborCost,
      sub: f.subcontractorPayout,
      margin: f.marginEstimate,
      marginPct: f.contractValue > 0 ? (f.marginEstimate / f.contractValue) * 100 : 0,
      paymentStatus: f.paymentStatus,
      // payoutStatus: f.payoutStatus,
      liveStatus: live.status,
      liveDetail: live.detail,
      holdReasons: live.holdReasons,
      heldAge: live.heldAge,
    };
  });

  const severityRank: Record<LivePayoutStatus, number> = {
    held: 0, ready: 1, pending_wrap: 2, none: 3, released: 4,
  };
  const paymentRank = (s: string) => s === "Overdue" ? 0 : s === "Partial Payment" ? 1 : s === "Paid" ? 3 : 2;
  rows.sort((a, b) => {
    if (a.liveStatus === "held" && b.liveStatus === "held") {
      return (b.heldAge?.ageMs ?? 0) - (a.heldAge?.ageMs ?? 0);
    }
    const live = severityRank[a.liveStatus] - severityRank[b.liveStatus];
    if (live !== 0) return live;
    const pay = paymentRank(a.paymentStatus) - paymentRank(b.paymentStatus);
    if (pay !== 0) return pay;
    return b.contract - a.contract;
  });

  const totalCredits = credits.reduce((s, c) => s + c.creditAmount, 0);

  const branchMap = new Map<string, { revenue: number; margin: number; jobs: number }>();
  jobsWithFinancials.forEach((j) => {
    const b = branchMap.get(j.branch) || { revenue: 0, margin: 0, jobs: 0 };
    b.revenue += j.financials!.contractValue;
    b.margin += j.financials!.marginEstimate;
    b.jobs += 1;
    branchMap.set(j.branch, b);
  });

  const visibleJobIds = new Set(allJobs.map((j) => j.id));
  const ledgerCategoryRollup = new Map<string, { amount: number; count: number }>();
  let ledgerTotal = 0;
  let ledgerEntryCount = 0;
  if (ledgerAuthorized) {
    for ( const e of allLedgerEntries) {
      if (!e.jobId || !visibleJobIds.has(e.jobId)) continue;
      if (e.category === "sales_commission" && e.source !== "commission_engine") continue;
      if (!Number.isFinite(e.amountCents) || e.amountCents === 0) continue;
      const dollars = e.amountCents / 100;
      ledgerTotal += dollars;
      ledgerEntryCount += 1;
      const bucket = ledgerCategoryRollup.get(e.category) ?? { amount: 0, count: 0 };
      bucket.amount += dollars;
      bucket.count += 1;
      ledgerCategoryRollup.set(e.category, bucket);
    }
  }
  const ledgerRows = Array.from(ledgerCategoryRollup.entries())
    .sort((a, b) => b[1].amount - a[1].amount);
  const categoryLabel = (cat: string) => 
    cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const paymentTone = (s: string): "good" | "warn" | "bad" | "muted" =>
    s === "Paid" ? "good" : s === "Overdue" ? "bad" : s === "Partial Payment" ? "warn" : "muted";
  const liveTone = (s: LivePayoutStatus): "good" | "warn" | "bad" | "muted" =>
    s === "released" ? "good"
    : s === "ready" ? "warn"
    : s === "held" ? "bad"
    : "muted";
  const liveLabel = (s: LivePayoutStatus): string =>
    s === "released" ? "Released"
    : s === "ready" ? "Ready"
    : s === "held" ? "Held"
    : s === "pending_wrap" ? "Pending wrap"
    : "—";

  const columns: CockpitColumn<Row>[] = [
    { key: "id", label: "Job", render: (r) => (
      <Link to={`/jobs/${r.id}`} className="font-medium text-foreground hover:underline">{r.id}</Link>
    )},
    { key: "customer", label: "Customer", render: (r) => (
      <Link to={`/customers/${encodeURIComponent(r.customerName)}`} className="text-foreground/90 hover:text-foreground">{r.customerName}</Link>
    )},
    { key: "branch", label: "Branch", render: (r) => <span className="text-muted-foreground">{r.branch}</span> },
    { key: "contract", label: "Contract", numeric: true, render: (r) => <span className="font-medium">{fmt(r.contract)}</span> },
    { key: "materials", label: "Materials", numeric: true, render: (r) => <span className="text-muted-foreground">{fmt(r.materials)}</span> },
    { key: "labor", label: "Labor", numeric: true, render: (r) => <span className="text-muted-foreground">{fmt(r.labor)}</span> },
    { key: "sub", label: "Sub Payout", numeric: true, render: (r) => (
      <span className="text-muted-foreground">{r.sub > 0 ? fmt(r.sub) : "—"}</span>
    )},
    { key: "margin", label: "Margin", numeric: true, render: (r) => (
      <span className={r.marginPct > 30 ? "text-success font-medium" : r.marginPct > 15 ? "text-foreground" : "text-warning"}>
        {fmt(r.margin)} <span className="text-muted-foreground/70">({r.marginPct.toFixed(0)}%)</span>
      </span>
    )},
    { key: "payment", label: "Payment", render: (r) => (
      <span className="inline-flex items-center gap-1.5 text-xs">
        <StatusDot tone={paymentTone(r.paymentStatus)} /> {r.paymentStatus}
      </span>
    )},
    { key: "payout", label: "Pay Run", render: (r) => {
      if (r.liveStatus === "none") return <span className="text-muted-foreground">—</span>;
      const href = r.liveStatus === "released" ? "/finance/pay-runs?pivot=paid"
        : r.liveStatus === "held" ? "/finance/pay-runs?pivot=held"
        : "/finance/pay-runs";
      // B4 — held aging tone (good/warn/bad/critical). Drives the header tint.
      const ageTone = r.heldAge ? getHeldAgeTone(r.heldAge.ageMs) : null;
      const ageColor = ageTone === "critical" ? "text-destructive font-medium"
        : ageTone === "warn" ? "text-warning"
        : "text-muted-foreground";
      // V2 — collapse hold reasons into a single compact pill with a tooltip-popover.
      // Keeps the Pay Run column legible; full detail on hover.
      const reasonCount = r.holdReasons.length;
      const topReason = r.holdReasons[0];
      return (
        <div className="inline-flex flex-col gap-1 text-xs">
          <Link to={href} className="inline-flex items-center gap-1.5 hover:opacity-80">
            <StatusDot tone={liveTone(r.liveStatus)} />
            <span>{liveLabel(r.liveStatus)}</span>
            {r.liveStatus === "held" && r.heldAge && (
              <span className={`text-[10px] tabular-nums ${ageColor}`}>· {formatHeldAge(r.heldAge.ageMs)}</span>
            )}
          </Link>
          {r.liveStatus === "held" && reasonCount > 0 ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={href}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-warning/40 text-warning text-[10px] leading-none hover:bg-warning/10 transition-colors self-start"
                  >
                    <span className="font-medium">{reasonCount} hold{reasonCount === 1 ? "" : "s"}</span>
                    <span className="text-muted-foreground/80 truncate max-w-[120px]">· {HOLD_REASON_LABEL[topReason]}</span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs p-2">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Hold reasons</p>
                  <ul className="space-y-1">
                    {r.holdReasons.map((k) => (
                      <li key={k} className="text-xs flex items-baseline gap-1.5">
                        <span className="font-medium">{HOLD_REASON_LABEL[k]}</span>
                        <span className="text-muted-foreground text-[10px]">· {HOLD_REASON_OWNER[k]}</span>
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-[10px] text-muted-foreground tabular-nums">{r.liveDetail}</span>
          )}
        </div>
      );
    } },
    
  ];

  const tFiltered = {
    contract: rows.reduce((s, r) => s + r.contract, 0),
    materials: rows.reduce((s, r) => s + r.materials, 0),
    labor: rows.reduce((s, r) => s + r.labor, 0),
    sub: rows.reduce((s, r) => s + r.sub, 0),
    margin: rows.reduce((s, r) => s + r.margin, 0),
  };

  const visibleJobIdSet = new Set(allJobs.map((j) => j.id));
  const scopedPayRunRows = payRunRows.filter((r) => visibleJobIdSet.has(r.job.id));
  const scopedReady = readyRows.filter((r) => visibleJobIdSet.has(r.job.id));
  const scopedHeld = heldRows.filter((r) => visibleJobIdSet.has(r.job.id));
  const readyAmount = scopedReady.reduce((s, r) => s + r.view.netReleasable, 0);
  // C1 — match PayRunsCockpit's Held dollar definition (gross at-risk = base + pending field-adds).
  // Single source of truth: same row set, same formula, both surfaces.
  const heldAmount = scopedHeld.reduce(
    (s, r) => s + r.view.baseAmount + r.view.fieldAddPendingTotal, 0,
  );

  const pendingFieldAdds = getPendingFieldAdds(fieldAddSubmissions).filter((f) => visibleJobIdSet.has(f.jobId));
  const pendingFieldAddAmount = pendingFieldAdds.reduce((s, f) => s + f.amount, 0);

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const releasedThisMonth = payRunRecords.filter(
    (r) => visibleJobIdSet.has(r.jobId) && r.releasedAt >= monthStart,
  );
  const releasedMtdAmount = releasedThisMonth.reduce((s, r) => s + r.netReleased, 0);

  // ── Phase B · Realized vs Estimated reconciliation ─────────────────
  const laborEstTotal = scopedPayRunRows.reduce((s, r) => s + (r.view.labor.estimated ?? 0), 0);
  const laborActualTotal = scopedPayRunRows.reduce((s, r) => s + (r.view.labor.actual ?? 0), 0);
  const laborJobsWithActuals = scopedPayRunRows.filter((r) => r.view.labor.actual != null).length;
  const laborDeltaPct = laborEstTotal > 0 ? ((laborActualTotal - laborEstTotal) / laborEstTotal) * 100 : 0;

  const approvedFieldAddTotal = scopedPayRunRows.reduce((s, r) => s + r.view.fieldAddApprovedTotal, 0);
  const approvedFieldAddJobs = scopedPayRunRows.filter((r) => r.view.fieldAddApprovedTotal > 0).length;

  const holdbackStates = deriveAllHoldbackStates(holdbackLedger);
  const activeHoldbacks = holdbackStates.filter(
    (h) => h.status === "active" && visibleJobIdSet.has(h.jobId),
  );
  const holdbackAmount = activeHoldbacks.reduce((s, h) => s + h.netHeld, 0);

  const disputeStates = deriveAllDisputes(disputeEvents);
  const openDisputes = disputeStates.filter(
    (d) => d.status === "open" && visibleJobIdSet.has(d.jobId),
  );

  return (
    <CockpitPage>
      {/* <CockpitMasthead
        title="Accounting Visibility"
        right={
          <>
            <HeroMetric label="Revenue" value={fmt(totalRevenue)} hero />
            <HeroDivider />
            <HeroMetric label="Margin" value={`${marginPct}%`} tone={Number(marginPct) >= 25 ? "good" : Number(marginPct) >= 15 ? "default" : "warn"} />
            <HeroDivider />
            <HeroMetric label="Overdue" value={overdue.length} tone={overdue.length > 0 ? "bad" : "good"} />
          </>
        }
      />

      <CockpitKpiStrip
        items={[
          { label: "Materials Cost", value: fmt(totalMaterialCost) },
          { label: "Labor + Sub Cost", value: fmt(totalLaborCost + totalSubPayout) },
          { label: "Est. Margin", value: fmt(totalMargin), tone: "good" },
          { label: "Credits Issued", value: fmt(totalCredits), tone: totalCredits > 0 ? "warn" : "default" },
          { label: "Jobs With Financials", value: jobsWithFinancials.length },
        ]}
      />

      {branchMap.size > 1 && (
        <CockpitSection eyebrow="Distribution" title="Revenue by Branch">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60 border border-border/60">
            {Array.from(branchMap.entries()).map(([branch, data]) => (
              <div key={branch} className="bg-background px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">{branch}</p>
                <p className="text-base font-semibold tabular-nums text-foreground">{fmt(data.revenue)}</p>
                <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
                  {data.jobs} jobs · {data.revenue > 0 ? ((data.margin / data.revenue) * 100).toFixed(0) : 0}% margin
                </p>
              </div>
            ))}
          </div>
        </CockpitSection>
      )} */}
      <BooksNavRail />

      <FinanceCommandCenter
        jobs={allJobs}
        payRunRecords={payRunRecords}
        heldRows={heldRows}
        overdueCount={overdue.length}
        overdueAmount={overdue.reduce((s, j) => s + j.financials!.contractValue, 0)}
        pendingFieldAddsCount={getPendingFieldAdds(fieldAddSubmissions).length}
        pendingFieldAddsAmount={getPendingFieldAdds(fieldAddSubmissions).reduce((s, f) => s + f.amount, 0)}
      />

      {fin$.viewFinancialSummary && (
        <OperationalLedger
          readyAmount={readyAmount}
          readyCount={scopedReady.length}
          heldAmount={heldAmount}
          heldCount={scopedHeld.length}
          pendingFieldAddAmount={pendingFieldAddAmount}
          pendingFieldAddCount={pendingFieldAdds.length}
          releasedMtdAmount={releasedMtdAmount}
          releasedMtdCount={releasedThisMonth.length}
          varianceMeasurable={laborJobsWithActuals > 0}
          laborDeltaPct={laborDeltaPct}
          laborJobsWithActuals={laborJobsWithActuals}
          laborTotalJobs={scopedPayRunRows.length}
          approvedFieldAddTotal={approvedFieldAddTotal}
          approvedFieldAddJobs={approvedFieldAddJobs}
          holdbackAmount={holdbackAmount}
          holdbackCount={activeHoldbacks.length}
          openDisputeCount={openDisputes.length}
          ledgerAuthorized={ledgerAuthorized}
          ledgerRows={ledgerRows}
          ledgerTotal={ledgerTotal}
          ledgerEntryCount={ledgerEntryCount}
          categoryLabel={categoryLabel}  
        />
      )}

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search job, customer, branch…" />}
        chips={
          <CockpitChipGroup
            value={activeFilter}
            onChange={(v) => setActiveFilter(v as FilterKey)}
            options={[
              { value: "all", label: "All", count: jobsWithFinancials.length },
              { value: "unpaid", label: "Unpaid", count: unpaid.length },
              { value: "overdue", label: "Overdue", count: overdue.length },
              { value: "held", label: "Held", count: heldJobs.length },
              { value: "payout-pending", label: "Payout Pending", count: payoutPending.length },
              { value: "paid", label: "Paid", count: paid.length },
            ]}
          />
        }
        // right={<BranchRegionFilter jobs={jobs} value={brFilter} onChange={setBrFilter} />}
        right={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              title="Export the filtered rows as CSV"
              onClick={() => exportCsv(
                stampFilename("accounting_jobs"),
                ["Job ID", "Customer", "Branch", "Contract", "Materials", "Labor", "Sub Payout", "Margin", "Margin %", "Payment", "Pay Run", "Held Age", "Hold Reasons", "Hold Owners"],
                rows.map((r) => [
                  r.id, r.customerName, r.branch,
                  r.contract, r.materials, r.labor, r.sub, r.margin, r.marginPct.toFixed(1),
                  r.paymentStatus, liveLabel(r.liveStatus),
                  r.heldAge ? formatHeldAge(r.heldAge.ageMs) : "",
                  r.holdReasons.map((k) => HOLD_REASON_LABEL[k]).join(" . "),
                  r.holdReasons.map((k) => HOLD_REASON_OWNER[k]).join(" . "),
                ])
              )}
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
            <BranchRegionFilter jobs={jobs} value={brFilter} onChange={setBrFilter} />
          </div>
        }
      />

      <CockpitFinancialTable
        columns={columns}
        rows={rows}
        getKey={(r) => r.id}
        empty="No financial records match the current filters."
        footer={
          <>
            <td className="py-3 px-4" colSpan={3}>Totals · {rows.length} jobs</td>
            <td className="py-3 px-4 text-right">{fmt(tFiltered.contract)}</td>
            <td className="py-3 px-4 text-right text-muted-foreground">{fmt(tFiltered.materials)}</td>
            <td className="py-3 px-4 text-right text-muted-foreground">{fmt(tFiltered.labor)}</td>
            <td className="py-3 px-4 text-right text-muted-foreground">{fmt(tFiltered.sub)}</td>
            <td className="py-3 px-4 text-right text-success">{fmt(tFiltered.margin)}</td>
            <td colSpan={2} />
          </>
        }
      />
      <VarianceDetailDrawer
        open={varianceDetail != null}
        onOpenChange={(o) => { if (!o) setVarianceDetail(null); }}
        context={varianceDetail}
      />
    </CockpitPage>
  );
}
